import React, { useEffect, useMemo, useState } from 'react';
import { Archive, ArchiveRestore, BookOpen, ChevronDown, ChevronRight, Copy, GraduationCap, Mail, Plus, RefreshCw, Save, Trash2, UserPlus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface AppClass {
  id: string;
  name: string;
  code: string;
  created_by: string;
  created_at: string;
  member_count: number;
  unlocked_chapter_count: number;
  archived_at?: string | null;
}

interface ClassMemberOverview {
  user_id: string;
  display_name: string | null;
  email: string | null;
  joined_at: string;
  total_attempts: number;
  correct_attempts: number;
  accuracy: number;
  last_active: string | null;
}

interface VocabBook {
  id: string;
  slug: string;
  name: string;
  position: number;
  parent_id: string | null;
}

interface VocabChapter {
  id: string;
  book_id: string;
  name: string;
  position: number;
}

interface ClassUnlockedChapter {
  class_id: string;
  chapter_id: string;
  unlocked_at: string;
}

type Feedback = {
  type: 'success' | 'error';
  message: string;
} | null;

interface Props {
  user: User | null;
  cardClass: string;
  inputClass: string;
  primaryBtn: string;
  secondaryBtn: string;
  showConfirmDialog: (message: string, action: () => void) => void;
  formatDate: (dateString: string) => string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim();
    if (message) return `${fallback} (${message})`;
  }
  return fallback;
};

// Supabase/PostgREST liefert pro Anfrage höchstens 1000 Zeilen. Kapitel werden
// über alle Bücher hinweg nach position sortiert — ohne Paging fehlt deshalb bei
// jedem Buch der hintere Teil (z. B. nur Kapitel 1–24 statt 1–31).
const SUPABASE_PAGE_SIZE = 1000;

type PagedResponse<T> = PromiseLike<{ data: T[] | null; error: unknown }>;

const fetchAllRows = async <T,>(fetchPage: (from: number, to: number) => PagedResponse<T>): Promise<T[]> => {
  const all: T[] = [];
  for (let from = 0; ; from += SUPABASE_PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + SUPABASE_PAGE_SIZE - 1);
    if (error) throw error;
    const rows = data ?? [];
    all.push(...rows);
    if (rows.length < SUPABASE_PAGE_SIZE) return all;
  }
};

const AppClassesManager = ({
  user,
  cardClass,
  inputClass,
  primaryBtn,
  secondaryBtn,
  showConfirmDialog,
  formatDate,
}: Props) => {
  const [appClasses, setAppClasses] = useState<AppClass[]>([]);
  const [classMembers, setClassMembers] = useState<Record<string, ClassMemberOverview[]>>({});
  const [openClassIds, setOpenClassIds] = useState<Record<string, boolean>>({});
  const [classUnlockedChapters, setClassUnlockedChapters] = useState<Record<string, Set<string>>>({});
  const [vocabBooks, setVocabBooks] = useState<VocabBook[]>([]);
  const [vocabChapters, setVocabChapters] = useState<VocabChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState<Record<string, boolean>>({});
  const [savingAction, setSavingAction] = useState<string | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [memberSearchByClass, setMemberSearchByClass] = useState<Record<string, string>>({});
  const [openMemberDetails, setOpenMemberDetails] = useState<Record<string, boolean>>({});
  const [openUnlockBooks, setOpenUnlockBooks] = useState<Record<string, boolean>>({});
  const [showArchivedClasses, setShowArchivedClasses] = useState(false);
  const [emailInviteClassId, setEmailInviteClassId] = useState<string | null>(null);
  const [emailInviteValue, setEmailInviteValue] = useState('');
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  const [renamingClassId, setRenamingClassId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
  };

  useEffect(() => {
    loadAll();
  }, [showArchivedClasses]);

  const chaptersByBook = useMemo(() => {
    return vocabBooks.map((book) => ({
      book,
      chapters: vocabChapters
        .filter((chapter) => chapter.book_id === book.id)
        .sort((a, b) => a.position - b.position),
    }));
  }, [vocabBooks, vocabChapters]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [classesResponse, books, chapters] = await Promise.all([
        supabase.rpc('get_admin_classes', { p_include_archived: showArchivedClasses }),
        fetchAllRows<VocabBook>((from, to) =>
          supabase.from('books').select('*').order('position', { ascending: true }).order('id').range(from, to)
        ),
        // Nach book_id gruppiert sortieren, damit das Paging stabil bleibt:
        // gleiche position in mehreren Büchern ist sonst nicht eindeutig.
        fetchAllRows<VocabChapter>((from, to) =>
          supabase
            .from('chapters')
            .select('*')
            .order('book_id')
            .order('position', { ascending: true })
            .order('id')
            .range(from, to)
        ),
      ]);

      if (classesResponse.error) throw classesResponse.error;

      const loadedClasses = (classesResponse.data ?? []) as AppClass[];
      setAppClasses(loadedClasses);
      setVocabBooks(books);
      setVocabChapters(chapters);

      // Ensure portal memberships are synced for existing legacy classes as soon as admin opens this area.
      await Promise.all(loadedClasses.map(c => loadClassMembers(c.id, true)));
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'App-Klassen konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  };

  const loadAppClasses = async () => {
    try {
      const { data, error } = await supabase.rpc('get_admin_classes', { p_include_archived: showArchivedClasses });
      if (error) throw error;
      setAppClasses((data ?? []) as AppClass[]);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Klassen konnten nicht aktualisiert werden.'));
    }
  };


  const syncPortalMembersForClass = async (legacyClassName: string, members: ClassMemberOverview[]) => {
    if (!legacyClassName || members.length === 0) return;

    const { data: portalClasses, error: portalClassError } = await supabase
      .from('portal_classes')
      .select('id, title, is_active, archived_at')
      .or(`title.eq.${legacyClassName},title.ilike.%${legacyClassName.replace(/[%_,]/g, '')}%`)
      .order('is_active', { ascending: false });

    if (portalClassError) {
      if (import.meta.env.DEV) console.error(portalClassError);
      return;
    }

    const matching = ((portalClasses ?? []) as Array<{ id: string; is_active: boolean; archived_at: string | null }>)
      .filter(c => c.is_active && !c.archived_at)
      .map(c => c.id);

    if (matching.length === 0) return;

    const rows = matching.flatMap(classId => members.map(m => ({ class_id: classId, student_id: m.user_id, is_active: true })));

    if (rows.length === 0) return;

    const { error } = await supabase
      .from('portal_class_members')
      .upsert(rows, { onConflict: 'class_id,student_id' });

    if (error) {
      if (import.meta.env.DEV) console.error(error);
    }
  };

  const loadClassMembers = async (classId: string, force = false) => {
    if (!force && classMembers[classId]) return;
    setLoadingMembers((prev) => ({ ...prev, [classId]: true }));
    try {
      const { data, error } = await supabase.rpc('get_class_members_overview', { p_class_id: classId });
      if (error) throw error;
      const members = (data ?? []) as ClassMemberOverview[];
      setClassMembers((prev) => ({ ...prev, [classId]: members }));
      const className = appClasses.find(c => c.id === classId)?.name ?? '';
      await syncPortalMembersForClass(className, members);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Schüler konnten nicht geladen werden.'));
    } finally {
      setLoadingMembers((prev) => ({ ...prev, [classId]: false }));
    }
  };

  const loadUnlockedChaptersForClass = async (classId: string, force = false) => {
    if (!force && classUnlockedChapters[classId]) return;
    try {
      // Auch hier gepaged: eine Klasse mit vielen Freischaltungen würde sonst
      // ab Zeile 1000 fälschlich als „gesperrt" angezeigt.
      const rows = await fetchAllRows<ClassUnlockedChapter>((from, to) =>
        supabase
          .from('class_unlocked_chapters')
          .select('class_id, chapter_id, unlocked_at')
          .eq('class_id', classId)
          .order('chapter_id')
          .range(from, to)
      );

      setClassUnlockedChapters((prev) => ({
        ...prev,
        [classId]: new Set(rows.map((item) => item.chapter_id)),
      }));
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Freigeschaltete Kapitel konnten nicht geladen werden.'));
    }
  };

  const toggleClassOpen = async (classId: string) => {
    const willOpen = !openClassIds[classId];
    setOpenClassIds((prev) => ({ ...prev, [classId]: willOpen }));
    if (willOpen) {
      await Promise.all([
        loadClassMembers(classId),
        loadUnlockedChaptersForClass(classId),
      ]);
    }
  };

  const generateClassCode = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'AR-';
    for (let i = 0; i < 5; i += 1) {
      code += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return code;
  };

  const createAppClass = async () => {
    if (!user) return;
    const name = newClassName.trim();
    if (!name) {
      showFeedback('error', 'Bitte gib einen Klassennamen ein.');
      return;
    }

    setSavingAction('create-class');
    try {
      const { error } = await supabase.from('classes').insert({
        name,
        code: generateClassCode(),
        created_by: user.id,
      });

      if (error) throw error;
      setNewClassName('');
      await loadAppClasses();
      showFeedback('success', `Klasse „${name}" wurde erstellt.`);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Klasse konnte nicht erstellt werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const startRename = (appClass: AppClass) => {
    setRenamingClassId(appClass.id);
    setRenameValue(appClass.name);
  };

  const renameClassWithPrompt = async (appClass: AppClass) => {
    if (!user) return;
    const nextName = renameValue.trim();
    if (!nextName || nextName === appClass.name) { setRenamingClassId(null); return; }
    setRenamingClassId(null);
    setSavingAction(`rename-class-${appClass.id}`);
    try {
      const { error } = await supabase
        .from('classes')
        .update({ name: nextName })
        .eq('id', appClass.id)
        .eq('created_by', user.id);

      if (error) throw error;
      setAppClasses((prev) => prev.map((item) => item.id === appClass.id ? { ...item, name: nextName } : item));
      showFeedback('success', 'Klasse wurde umbenannt.');
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Klasse konnte nicht umbenannt werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const copyClassCode = async (appClass: AppClass) => {
    try {
      await navigator.clipboard.writeText(appClass.code);
      setCopiedCodeId(appClass.id);
      showFeedback('success', `Code „${appClass.code}" wurde in die Zwischenablage kopiert.`);
      setTimeout(() => setCopiedCodeId(null), 2000);
    } catch {
      showFeedback('error', 'Code konnte nicht kopiert werden. Bitte manuell kopieren.');
    }
  };

  const archiveClass = async (appClass: AppClass) => {
    if (!user) return;
    setSavingAction(`archive-${appClass.id}`);
    try {
      const { error } = await supabase.from('classes').update({ archived_at: new Date().toISOString() }).eq('id', appClass.id).eq('created_by', user.id);
      if (error) throw error;
      await loadAppClasses();
      showFeedback('success', `Klasse „${appClass.name}" wurde archiviert.`);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Klasse konnte nicht archiviert werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const restoreClass = async (appClass: AppClass) => {
    if (!user) return;
    setSavingAction(`restore-${appClass.id}`);
    try {
      const { error } = await supabase.from('classes').update({ archived_at: null }).eq('id', appClass.id).eq('created_by', user.id);
      if (error) throw error;
      await loadAppClasses();
      showFeedback('success', `Klasse „${appClass.name}" wurde wiederhergestellt.`);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Klasse konnte nicht wiederhergestellt werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const deleteClass = async (appClass: AppClass) => {
    if (!user) return;
    setSavingAction(`delete-${appClass.id}`);
    try {
      const { error } = await supabase.from('classes').delete().eq('id', appClass.id).eq('created_by', user.id);
      if (error) throw error;
      setAppClasses(prev => prev.filter(c => c.id !== appClass.id));
      showFeedback('success', `Klasse „${appClass.name}" wurde gelöscht.`);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Klasse konnte nicht gelöscht werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const regenerateClassCode = async (appClass: AppClass) => {
    if (!user) return;
    setSavingAction(`regen-code-${appClass.id}`);
    try {
      const newCode = generateClassCode();
      const { error } = await supabase
        .from('classes')
        .update({ code: newCode })
        .eq('id', appClass.id)
        .eq('created_by', user.id);

      if (error) throw error;
      setAppClasses((prev) => prev.map((item) => item.id === appClass.id ? { ...item, code: newCode } : item));
      showFeedback('success', `Neuer Code: ${newCode}`);
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Code konnte nicht neu generiert werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const addMemberByEmail = async (classId: string) => {
  const email = emailInviteValue.trim().toLowerCase();

  if (!email) {
    showFeedback('error', 'Bitte gib eine E-Mail-Adresse ein.');
    return;
  }

  setSavingAction(`add-email-${classId}`);

  try {
    const { error } = await supabase.rpc('add_member_to_class_by_email', {
      p_class_id: classId,
      p_email: email,
    });

    if (error) throw error;

    setEmailInviteValue('');
    setEmailInviteClassId(null);

    await Promise.all([
      loadClassMembers(classId, true),
      loadAppClasses(),
    ]);

    showFeedback('success', `Schüler mit E-Mail „${email}“ wurde zur Klasse hinzugefügt.`);
  } catch (error) {
    if (import.meta.env.DEV) console.error(error);

    const message = error instanceof Error ? error.message : String(error);

    if (message.includes('user_not_found')) {
      showFeedback('error', `Kein Benutzer mit der E-Mail „${email}“ gefunden. Der Schüler muss sich zuerst in der App registrieren.`);
    } else if (message.includes('not_allowed_or_class_not_found')) {
      showFeedback('error', 'Du darfst diese Klasse nicht bearbeiten oder sie ist archiviert.');
    } else {
      showFeedback('error', getErrorMessage(error, 'Schüler konnte nicht hinzugefügt werden.'));
    }
  } finally {
    setSavingAction(null);
  }
};

  const removeClassMember = async (classId: string, member: ClassMemberOverview) => {
    setSavingAction(`remove-member-${classId}-${member.user_id}`);
    try {
      const { error } = await supabase
        .from('class_members')
        .delete()
        .eq('class_id', classId)
        .eq('user_id', member.user_id);

      if (error) throw error;

      setClassMembers((prev) => ({
        ...prev,
        [classId]: (prev[classId] ?? []).filter((item) => item.user_id !== member.user_id),
      }));
      await loadAppClasses();
      showFeedback('success', 'Schüler wurde aus der Klasse entfernt.');
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Schüler konnte nicht entfernt werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const toggleChapterUnlock = async (classId: string, chapterId: string) => {
    const current = classUnlockedChapters[classId] ?? new Set<string>();
    const isUnlocked = current.has(chapterId);
    setSavingAction(`chapter-${classId}-${chapterId}`);

    try {
      if (isUnlocked) {
        const { error } = await supabase
          .from('class_unlocked_chapters')
          .delete()
          .eq('class_id', classId)
          .eq('chapter_id', chapterId);

        if (error) throw error;

        const next = new Set(current);
        next.delete(chapterId);
        setClassUnlockedChapters((prev) => ({ ...prev, [classId]: next }));
        showFeedback('success', 'Kapitel wurde entfernt.');
      } else {
        const { error } = await supabase
          .from('class_unlocked_chapters')
          .insert({ class_id: classId, chapter_id: chapterId });

        if (error) throw error;

        const next = new Set(current);
        next.add(chapterId);
        setClassUnlockedChapters((prev) => ({ ...prev, [classId]: next }));
        showFeedback('success', 'Kapitel wurde freigeschaltet.');
      }

      await loadAppClasses();
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Kapitel-Freischaltung konnte nicht gespeichert werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const setBookUnlock = async (classId: string, bookId: string, chapterIds: string[], unlock: boolean) => {
    const actionKey = `book-${classId}-${bookId}`;
    setSavingAction(actionKey);

    try {
      const { error } = await supabase.rpc('set_class_book_unlock', {
        p_class_id: classId,
        p_book_id: bookId,
        p_unlock: unlock,
      });

      if (error) throw error;

      setClassUnlockedChapters((prev) => {
        const current = prev[classId] ?? new Set<string>();
        const next = new Set(current);
        if (unlock) {
          chapterIds.forEach((chapterId) => next.add(chapterId));
        } else {
          chapterIds.forEach((chapterId) => next.delete(chapterId));
        }
        return { ...prev, [classId]: next };
      });

      await loadAppClasses();
      showFeedback('success', unlock ? 'Ganzes Buch wurde freigeschaltet.' : 'Ganzes Buch wurde entfernt.');
    } catch (error) {
      if (import.meta.env.DEV) console.error(error);
      showFeedback('error', getErrorMessage(error, 'Buch-Freischaltung konnte nicht gespeichert werden.'));
    } finally {
      setSavingAction(null);
    }
  };

  const formatOptionalDate = (value: string | null) => {
    if (!value) return 'Noch keine Aktivität';
    return formatDate(value);
  };

  const getMemberDetailKey = (classId: string, userId: string) => `${classId}-${userId}`;

  const toggleMemberDetails = (classId: string, userId: string) => {
    const key = getMemberDetailKey(classId, userId);
    setOpenMemberDetails((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getMemberStatus = (member: ClassMemberOverview) => {
    if (!member.last_active) return 'Noch nie aktiv';
    return 'Aktiv';
  };

  const getUnlockBookKey = (classId: string, bookId: string) => `${classId}-${bookId}`;

  const toggleUnlockBook = (classId: string, bookId: string) => {
    const key = getUnlockBookKey(classId, bookId);
    setOpenUnlockBooks((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const filterMembers = (classId: string, members: ClassMemberOverview[]) => {
    const query = (memberSearchByClass[classId] ?? '').trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) => {
      const name = (member.display_name ?? '').toLowerCase();
      const email = (member.email ?? '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  };

  if (loading) {
    return <div className={cardClass + ' p-8 text-center text-gray-500'}>App-Klassen werden geladen...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-2">App-Klassen</p>
          <h2 className="text-3xl font-bold text-[#1f1f1f]">Klassenverwaltung</h2>
          <p className="text-gray-500 mt-1">Klassen erstellen, Schüler ansehen und Kapitel freischalten.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-600">
            <input
              type="checkbox"
              checked={showArchivedClasses}
              onChange={(event) => setShowArchivedClasses(event.target.checked)}
              className="h-4 w-4 accent-[#970000]"
            />
            Archivierte anzeigen
          </label>
          <button onClick={loadAll} className={secondaryBtn}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </button>
        </div>
      </div>

      {feedback ? (
        <div className={`rounded-2xl border px-4 py-3 text-sm ${feedback.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <span>{feedback.message}</span>
            <button type="button" onClick={() => setFeedback(null)} className="text-xs font-semibold opacity-70 hover:opacity-100">Schließen</button>
          </div>
        </div>
      ) : null}

      <div className={cardClass + ' p-5'}>
        <div className="grid lg:grid-cols-[1fr_auto] gap-3">
          <input
            placeholder="Neue Klasse, z. B. Level 1 Männer Januar"
            value={newClassName}
            onChange={(event) => setNewClassName(event.target.value)}
            className={inputClass}
          />
          <button onClick={createAppClass} disabled={savingAction === 'create-class'} className={primaryBtn}>
            {savingAction === 'create-class' ? <Save className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Klasse erstellen
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">Der Klassen-Code wird automatisch erstellt und kann von Schülern in der App eingelöst werden.</p>
      </div>

      {appClasses.length === 0 ? (
        <div className={cardClass + ' p-8 text-center text-gray-500'}>Noch keine App-Klassen vorhanden.</div>
      ) : (
        <div className="space-y-4">
          {appClasses.map((appClass) => {
            const isOpen = !!openClassIds[appClass.id];
            const isArchived = !!appClass.archived_at;
            const members = classMembers[appClass.id] ?? [];
            const filteredMembers = filterMembers(appClass.id, members);
            const memberSearch = memberSearchByClass[appClass.id] ?? '';
            const unlocked = classUnlockedChapters[appClass.id] ?? new Set<string>();
            const renameSaving = savingAction === `rename-class-${appClass.id}`;
            const regenSaving = savingAction === `regen-code-${appClass.id}`;
            const archiveSaving = savingAction === `archive-${appClass.id}`;
            const restoreSaving = savingAction === `restore-${appClass.id}`;
            const deleteSaving = savingAction === `delete-${appClass.id}`;
            const isCopied = copiedCodeId === appClass.id;

            return (
              <div key={appClass.id} className={cardClass + ` overflow-hidden ${isArchived ? 'opacity-80' : ''}`}>
                <button
                  onClick={() => toggleClassOpen(appClass.id)}
                  className="w-full flex items-center gap-4 p-5 text-left hover:bg-[#faf8f6] transition-colors"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#970000]/10 flex items-center justify-center shrink-0">
                    <GraduationCap className="h-6 w-6 text-[#970000]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-bold text-lg text-[#1f1f1f] truncate">{appClass.name}</h3>
                      {isArchived ? <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600">Archiviert</span> : null}
                    </div>
                    <p className="text-sm text-gray-500">
                      Code: <span className="font-semibold text-[#970000]">{appClass.code}</span> · {appClass.member_count} Schüler · {appClass.unlocked_chapter_count} Kapitel
                    </p>
                  </div>
                  {isOpen ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-[#eadfd6] bg-[#fffdfb] p-5 space-y-6">
                    {/* Actions bar */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyClassCode(appClass); }}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-colors ${isCopied ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-gray-700 border-[#eadfd6] hover:bg-[#faf8f6]'}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                        {isCopied ? 'Kopiert!' : 'Code kopieren'}
                      </button>

                      {!isArchived && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); showConfirmDialog('Möchtest du einen neuen Klassen-Code generieren? Der alte Code wird ungültig.', () => regenerateClassCode(appClass)); }}
                          disabled={regenSaving}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border bg-white text-gray-700 border-[#eadfd6] hover:bg-[#faf8f6] transition-colors disabled:opacity-60"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${regenSaving ? 'animate-spin' : ''}`} />
                          {regenSaving ? 'Generiert...' : 'Code neu generieren'}
                        </button>
                      )}

                      {!isArchived && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEmailInviteClassId(emailInviteClassId === appClass.id ? null : appClass.id); setEmailInviteValue(''); }}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border bg-white text-gray-700 border-[#eadfd6] hover:bg-[#faf8f6] transition-colors"
                        >
                          <UserPlus className="h-3.5 w-3.5" />
                          Schüler per E-Mail hinzufügen
                        </button>
                      )}

                      {renamingClassId === appClass.id ? (
                        <form
                          onSubmit={(e) => { e.preventDefault(); renameClassWithPrompt(appClass); }}
                          className="flex items-center gap-1.5"
                          onClick={e => e.stopPropagation()}
                        >
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            className="px-2 py-1.5 border border-[#970000] rounded-lg text-xs w-36 outline-none"
                          />
                          <button type="submit" disabled={renameSaving} className="px-2 py-1.5 rounded-lg bg-[#970000] text-white text-xs font-semibold disabled:opacity-60">OK</button>
                          <button type="button" onClick={() => setRenamingClassId(null)} className="px-2 py-1.5 rounded-lg border border-[#eadfd6] text-xs text-gray-600">✕</button>
                        </form>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startRename(appClass); }}
                          disabled={renameSaving}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border bg-white text-gray-700 border-[#eadfd6] hover:bg-[#faf8f6] transition-colors disabled:opacity-60"
                        >
                          {renameSaving ? 'Umbenennen...' : 'Umbenennen'}
                        </button>
                      )}

                      {!isArchived ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); showConfirmDialog(`Klasse „${appClass.name}" archivieren? Schüler können weiterhin auf freigeschaltete Inhalte zugreifen, aber die Klasse wird nicht mehr aktiv verwaltet.`, () => archiveClass(appClass)); }}
                          disabled={archiveSaving}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-60"
                        >
                          <Archive className="h-3.5 w-3.5" />
                          {archiveSaving ? 'Archiviert...' : 'Archivieren'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); restoreClass(appClass); }}
                          disabled={restoreSaving}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border bg-green-50 text-green-700 border-green-200 hover:bg-green-100 transition-colors disabled:opacity-60"
                        >
                          <ArchiveRestore className="h-3.5 w-3.5" />
                          {restoreSaving ? 'Wird wiederhergestellt...' : 'Wiederherstellen'}
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); showConfirmDialog(`Klasse „${appClass.name}" endgültig löschen? Alle Schüler-Zuweisungen und Kapitel-Freischaltungen werden entfernt. Diese Aktion kann NICHT rückgängig gemacht werden!`, () => deleteClass(appClass)); }}
                        disabled={deleteSaving}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border bg-red-50 text-red-700 border-red-200 hover:bg-red-100 transition-colors disabled:opacity-60"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {deleteSaving ? 'Wird gelöscht...' : 'Löschen'}
                      </button>
                    </div>

                    {/* Email invite form */}
                    {emailInviteClassId === appClass.id && (
                      <div className="rounded-2xl border border-[#eadfd6] bg-white p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Mail className="h-4 w-4 text-[#970000]" />
                          <h4 className="font-semibold text-sm text-[#1f1f1f]">Schüler per E-Mail hinzufügen</h4>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">Der Schüler muss sich bereits in der App registriert haben.</p>
                        <div className="flex gap-2">
                          <input
                            type="email"
                            placeholder="E-Mail-Adresse des Schülers"
                            value={emailInviteValue}
                            onChange={(e) => setEmailInviteValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') addMemberByEmail(appClass.id); }}
                            className={inputClass + ' flex-1'}
                          />
                          <button
                            type="button"
                            onClick={() => addMemberByEmail(appClass.id)}
                            disabled={savingAction === `add-email-${appClass.id}`}
                            className={primaryBtn + ' whitespace-nowrap'}
                          >
                            {savingAction === `add-email-${appClass.id}` ? 'Wird hinzugefügt...' : 'Hinzufügen'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Members section */}
                    <div>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                        <h4 className="font-semibold text-[#1f1f1f]">Schüler</h4>
                        <input
                          placeholder="Schüler suchen..."
                          value={memberSearch}
                          onChange={(event) => setMemberSearchByClass((prev) => ({ ...prev, [appClass.id]: event.target.value }))}
                          className={inputClass + ' md:max-w-xs py-2'}
                        />
                      </div>
                      {loadingMembers[appClass.id] ? (
                        <p className="text-sm text-gray-500">Schüler werden geladen...</p>
                      ) : members.length === 0 ? (
                        <p className="text-sm text-gray-500">Noch keine Schüler in dieser Klasse.</p>
                      ) : filteredMembers.length === 0 ? (
                        <p className="text-sm text-gray-500">Keine Schüler zur Suche gefunden.</p>
                      ) : (
                        <div className="overflow-auto rounded-2xl border border-[#eadfd6] bg-white">
                          <table className="w-full text-sm">
                            <thead className="bg-[#faf8f6]">
                              <tr>
                                <th className="p-3 text-left">Schüler</th>
                                <th className="p-3 text-left">Attempts</th>
                                <th className="p-3 text-left">Trefferquote</th>
                                <th className="p-3 text-left">Zuletzt aktiv</th>
                                <th className="p-3 text-left">Aktionen</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredMembers.map((member) => {
                                const detailKey = getMemberDetailKey(appClass.id, member.user_id);
                                const detailsOpen = !!openMemberDetails[detailKey];

                                return (
                                  <React.Fragment key={member.user_id}>
                                    <tr className="border-t border-[#f0e8e1]">
                                      <td className="p-3">
                                        <div className="font-semibold text-[#1f1f1f]">{member.display_name || 'Ohne Namen'}</div>
                                        <div className="text-xs text-gray-500">{member.email || 'Keine E-Mail'}</div>
                                      </td>
                                      <td className="p-3">{member.correct_attempts}/{member.total_attempts}</td>
                                      <td className="p-3">{Number(member.accuracy ?? 0).toFixed(1)}%</td>
                                      <td className="p-3 text-gray-500">{formatOptionalDate(member.last_active)}</td>
                                      <td className="p-3">
                                        <div className="flex items-center gap-3">
                                          <button
                                            type="button"
                                            onClick={() => toggleMemberDetails(appClass.id, member.user_id)}
                                            className="text-xs font-semibold text-[#970000] hover:underline"
                                          >
                                            {detailsOpen ? 'Details schließen' : 'Details'}
                                          </button>

                                          {!isArchived ? (
                                            <button
                                              onClick={() => showConfirmDialog(
                                                `${member.display_name || member.email || 'Diesen Schüler'} aus der Klasse entfernen?`,
                                                () => removeClassMember(appClass.id, member)
                                              )}
                                              className="text-red-600 hover:text-red-800"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </button>
                                          ) : (
                                            <span className="text-xs text-gray-400">Archiviert</span>
                                          )}
                                        </div>
                                      </td>
                                    </tr>

                                    {detailsOpen ? (
                                      <tr className="border-t border-[#f0e8e1] bg-[#fffaf6]">
                                        <td colSpan={5} className="p-4">
                                          <div className="grid md:grid-cols-4 gap-3">
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">Name</p>
                                              <p className="font-semibold text-[#1f1f1f]">{member.display_name || 'Ohne Namen'}</p>
                                            </div>
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">E-Mail</p>
                                              <p className="font-semibold text-[#1f1f1f] break-all">{member.email || 'Keine E-Mail'}</p>
                                            </div>
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">Beigetreten</p>
                                              <p className="font-semibold text-[#1f1f1f]">{formatDate(member.joined_at)}</p>
                                            </div>
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">Status</p>
                                              <p className="font-semibold text-[#1f1f1f]">{getMemberStatus(member)}</p>
                                            </div>
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">Attempts gesamt</p>
                                              <p className="font-semibold text-[#1f1f1f]">{member.total_attempts}</p>
                                            </div>
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">Richtige Antworten</p>
                                              <p className="font-semibold text-[#1f1f1f]">{member.correct_attempts}</p>
                                            </div>
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">Trefferquote</p>
                                              <p className="font-semibold text-[#1f1f1f]">{Number(member.accuracy ?? 0).toFixed(1)}%</p>
                                            </div>
                                            <div className="rounded-xl bg-white border border-[#eadfd6] p-3">
                                              <p className="text-xs text-gray-500">Zuletzt aktiv</p>
                                              <p className="font-semibold text-[#1f1f1f]">{formatOptionalDate(member.last_active)}</p>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : null}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Chapter unlock section */}
                    <div>
                      <h4 className="font-semibold text-[#1f1f1f] mb-3">Kapitel freischalten</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {chaptersByBook.map(({ book, chapters }) => {
                          const chapterIds = chapters.map((chapter) => chapter.id);
                          const unlockedCount = chapterIds.filter((chapterId) => unlocked.has(chapterId)).length;
                          const allUnlocked = chapterIds.length > 0 && unlockedCount === chapterIds.length;
                          const bookSaving = savingAction === `book-${appClass.id}-${book.id}`;
                          const unlockBookKey = getUnlockBookKey(appClass.id, book.id);
                          const bookOpen = !!openUnlockBooks[unlockBookKey];

                          const unlockedPct = chapterIds.length > 0 ? (unlockedCount / chapterIds.length) * 100 : 0;

                          return (
                            <div key={book.id} className="rounded-2xl border border-[#eadfd6] bg-white p-4">
                              <div className="flex items-start justify-between gap-3 mb-2.5">
                                <button
                                  type="button"
                                  onClick={() => toggleUnlockBook(appClass.id, book.id)}
                                  className="flex items-center gap-2 min-w-0 text-left"
                                >
                                  {bookOpen ? (
                                    <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                                  )}
                                  <span className="w-8 h-8 rounded-xl bg-[#970000]/[0.08] flex items-center justify-center shrink-0">
                                    <BookOpen className="h-4 w-4 text-[#970000]" />
                                  </span>
                                  <h5 className="font-semibold text-[#1f1f1f] truncate">{book.name}</h5>
                                </button>
                                {!isArchived ? (
                                  <button
                                    type="button"
                                    disabled={bookSaving || chapterIds.length === 0}
                                    onClick={() => setBookUnlock(appClass.id, book.id, chapterIds, !allUnlocked)}
                                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                                      allUnlocked
                                        ? 'bg-[#970000] text-white border-[#970000] hover:bg-[#7a0000]'
                                        : 'bg-white text-[#970000] border-[#970000]/30 hover:bg-[#970000]/5'
                                    } ${bookSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                                  >
                                    {bookSaving ? 'Speichert…' : allUnlocked ? 'Alles entfernen' : 'Alles freischalten'}
                                  </button>
                                ) : null}
                              </div>

                              {/* Fortschritt */}
                              <div className="flex items-center gap-2.5">
                                <div className="flex-1 h-1.5 rounded-full bg-[#f0e7df] overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-[#970000] transition-all duration-300"
                                    style={{ width: `${unlockedPct}%` }}
                                  />
                                </div>
                                <span className="text-[11px] font-semibold text-[#9b9490] shrink-0 tabular-nums">
                                  {unlockedCount}/{chapterIds.length} frei
                                </span>
                              </div>

                              {bookOpen ? (
                                <div className="flex flex-wrap gap-1.5 pt-3 max-h-64 overflow-auto pr-1">
                                  {chapters.map((chapter) => {
                                    const checked = unlocked.has(chapter.id);
                                    const saving = savingAction === `chapter-${appClass.id}-${chapter.id}`;

                                    return (
                                      <button
                                        key={chapter.id}
                                        type="button"
                                        disabled={saving || bookSaving || isArchived}
                                        onClick={() => toggleChapterUnlock(appClass.id, chapter.id)}
                                        title={checked ? 'Kapitel sperren' : 'Kapitel freischalten'}
                                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                          checked
                                            ? 'bg-[#970000] text-white border-[#970000] hover:bg-[#7a0000]'
                                            : 'bg-white text-[#5f5148] border-[#ddd6cf] hover:border-[#970000]/40 hover:text-[#970000]'
                                        }`}
                                      >
                                        {chapter.name}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AppClassesManager;
